<template>
  <div>
    <v-card class="pl-5 pr-5 pb-5">
      <h1>LTI Token Viewer</h1>
      <v-divider></v-divider>

      <div v-if="error" class="mt-5">
        <v-alert type="error">{{ error }}</v-alert>
      </div>

      <div v-else-if="!token" class="mt-5">
        <v-alert type="warning">No token provided. Use <code>?token=&lt;jwt&gt;</code> in the URL.</v-alert>
      </div>

      <div v-else class="mt-4">
        <!-- Header -->
        <v-expansion-panels accordion>
          <v-expansion-panel>
            <v-expansion-panel-header class="font-weight-bold">
              <span><v-icon class="mr-2" color="grey">mdi-lock-outline</v-icon>Header</span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-simple-table dense>
                <template v-slot:default>
                  <tbody>
                    <tr v-for="(value, key) in header" :key="key">
                      <td class="font-weight-medium" style="width:220px">{{ key }}</td>
                      <td class="token-value">{{ value }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <!-- Payload -->
          <v-expansion-panel>
            <v-expansion-panel-header class="font-weight-bold">
              <span><v-icon class="mr-2" color="primary">mdi-file-document-outline</v-icon>Payload</span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-simple-table dense>
                <template v-slot:default>
                  <tbody>
                    <tr v-for="(value, key) in flatPayload" :key="key">
                      <td class="font-weight-medium claim-key" style="width:220px">
                        <span :title="key">{{ friendlyKey(key) }}</span>
                        <div class="caption grey--text">{{ key }}</div>
                      </td>
                      <td class="token-value">
                        <span v-if="isObject(value)">
                          <pre class="pre-wrap">{{ JSON.stringify(value, null, 2) }}</pre>
                        </span>
                        <span v-else-if="isTimestamp(key)">
                          {{ value }} <span class="caption grey--text ml-2">({{ formatTimestamp(value) }})</span>
                        </span>
                        <span v-else>{{ value }}</span>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <!-- Raw Token -->
          <v-expansion-panel>
            <v-expansion-panel-header class="font-weight-bold">
              <span><v-icon class="mr-2" color="grey">mdi-code-braces</v-icon>Raw Token</span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-textarea
                :value="token"
                readonly
                outlined
                dense
                auto-grow
                class="mt-2 token-raw"
              ></v-textarea>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- Token status bar -->
        <div class="d-flex align-center mt-4 ml-1">
          <v-chip small :color="expired ? 'error' : 'success'" text-color="white" class="mr-3">
            <v-icon small left>{{ expired ? 'mdi-clock-alert-outline' : 'mdi-check-circle-outline' }}</v-icon>
            {{ expired ? 'Expired' : 'Valid' }}
          </v-chip>
          <span v-if="payload.exp" class="caption grey--text">
            {{ expired ? 'Expired' : 'Expires' }} {{ formatTimestamp(payload.exp) }}
          </span>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
const CLAIM_LABELS = {
  iss: "Issuer",
  sub: "Subject",
  aud: "Audience",
  exp: "Expiration Time",
  iat: "Issued At",
  nbf: "Not Before",
  jti: "JWT ID",
  nonce: "Nonce",
  "https://purl.imsglobal.org/spec/lti/claim/message_type": "Message Type",
  "https://purl.imsglobal.org/spec/lti/claim/version": "LTI Version",
  "https://purl.imsglobal.org/spec/lti/claim/deployment_id": "Deployment ID",
  "https://purl.imsglobal.org/spec/lti/claim/target_link_uri": "Target Link URI",
  "https://purl.imsglobal.org/spec/lti/claim/resource_link": "Resource Link",
  "https://purl.imsglobal.org/spec/lti/claim/roles": "Roles",
  "https://purl.imsglobal.org/spec/lti/claim/context": "Context",
  "https://purl.imsglobal.org/spec/lti/claim/platform": "Platform",
  "https://purl.imsglobal.org/spec/lti/claim/lis": "LIS",
  "https://purl.imsglobal.org/spec/lti/claim/launch_presentation": "Launch Presentation",
  "https://purl.imsglobal.org/spec/lti/claim/custom": "Custom Claims",
  "https://purl.imsglobal.org/spec/lti-ags/claim/endpoint": "AGS Endpoint",
  "https://purl.imsglobal.org/spec/lti-nrps/claim/namesroleservice": "Names & Role Service",
  given_name: "First Name",
  family_name: "Last Name",
  name: "Full Name",
  email: "Email",
  picture: "Picture URL",
  locale: "Locale",
};

const TIMESTAMP_KEYS = ["exp", "iat", "nbf"];

export default {
  name: "LtiTokenViewer",
  created() {
    this.token = this.$route.query.token || "";
    if (this.token) {
      this.decodeToken();
    }
  },
  methods: {
    decodeToken() {
      try {
        const parts = this.token.split(".");
        if (parts.length < 2) throw new Error("Not a valid JWT (expected 3 parts).");
        this.header = JSON.parse(atob(this.padBase64(parts[0])));
        this.payload = JSON.parse(atob(this.padBase64(parts[1])));
        this.flatPayload = this.payload;
        if (this.payload.exp) {
          this.expired = Date.now() / 1000 > this.payload.exp;
        }
      } catch (e) {
        this.error = "Failed to decode token: " + e.message;
      }
    },
    padBase64(str) {
      return str.replace(/-/g, "+").replace(/_/g, "/").padEnd(str.length + (4 - (str.length % 4)) % 4, "=");
    },
    friendlyKey(key) {
      return CLAIM_LABELS[key] || key;
    },
    isObject(value) {
      return value !== null && typeof value === "object";
    },
    isTimestamp(key) {
      return TIMESTAMP_KEYS.includes(key);
    },
    formatTimestamp(unix) {
      return new Date(unix * 1000).toLocaleString();
    },
  },
  data: () => ({
    token: "",
    header: {},
    payload: {},
    flatPayload: {},
    expired: false,
    error: "",
  }),
};
</script>

<style scoped>
.token-value {
  word-break: break-all;
  font-size: 13px;
}
.claim-key {
  vertical-align: top;
  padding-top: 10px !important;
}
.pre-wrap {
  white-space: pre-wrap;
  font-size: 12px;
  background: #f5f5f5;
  padding: 8px;
  border-radius: 4px;
}
.token-raw textarea {
  font-size: 12px;
  font-family: monospace;
}
</style>



// WEBPACK FOOTER //
// src/views/LtiTokenViewer.vue